import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QUERY_KEYS } from 'constants/common';
import { PatientService } from 'services/patient';
import { type GetActivePatientRecordParams, type PatientRecord } from 'types/patient';

export const useFetchActivePatientRecord = (
  patientId: string,
  params?: GetActivePatientRecordParams
): UseQueryResult<PatientRecord | null> => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);

  return useQuery({
    queryKey: [QUERY_KEYS.FETCH_PATIENT_ACTIVE_RECORD, patientId, params?.contractId],
    queryFn: async (): Promise<PatientRecord | null> => {
      try {
        const response = await PatientService.getActiveRecord(patientId, params);

        if (response.status === 204 || response.status === 409 || response.status === 406) {
          if (response.status === 406) {
            toast.error(t('PatientRecords.error.notInsurance'));
          }
          if (response.status === 409) {
            toast.error(t('PatientRecords.error.multipleActive'));
          }
          return null;
        }

        if (!response.data || typeof response.data === 'string') return null;
        return response.data;
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response?.status === 406) {
            toast.error(t('PatientRecords.error.notInsurance'));
            return null;
          }
          if (error.response?.status === 409) {
            toast.error(t('PatientRecords.error.multipleActive'));
            return null;
          }
        }
        throw error;
      }
    },
    enabled: !!patientId,
  });
};
